import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import moment from 'moment'
import { withOfferPageAndReduxState } from 'contexts/OfferPage/withOfferPageState'
import OfferCalendarMonthPicker from '../MonthPicker/OfferCalendarMonthPicker'
import useOfferPageDispatch from 'hooks/OfferPage/useOfferPageDispatch'
import { OfferPageState, OfferPageActions, OfferPageFilterTypes, PaginatorState } from 'contexts/OfferPage/offerPageStateReducer'
import OfferCalendarSidebarDayPicker from '../DayPicker/OfferCalendarSidebarDayPicker'
import { getCalendarMonths, getSelectedOfferPageDay, isFetchingCalendars } from 'selectors/multipleRoomBookingSelectors'
import OfferCalendarNotAvailableMessage from '../OfferCalendarNotAvailableMessage'
import { CALENDAR_GA_EVENT_ACTION } from 'constants/analytics'
import OfferCalendarDurationSelect from '../CalendarDuration/OfferCalendarDurationSelect'
import UserAnalyticsActionContext from 'contexts/userAnalyticsActionContext'
import { shouldEnableCalendarRecommendations } from 'selectors/featuresSelectors'
import CalendarPickerRecommendationsSidebar from '../Recommendations/CalendarPickerRecommendationsSidebar'
import useDispatchPrecacheLere from 'components/Recommendations/hooks/useDispatchPrecacheLere'
import { takeCurrentOfferState } from 'contexts/OfferPage/offerPageStateReducer'
import Group from 'components/utils/Group'
import OfferCalendarBundlePackageSelect from '../CalendarBundlePackages/OfferCalendarBundlePackageSelect'
import LoadingIndicator from 'components/Common/Loading/LoadingIndicator'
import ModalHeader from 'components/Luxkit/Modal/ModalHeader'
import ModalBody from 'components/Luxkit/Modal/ModalBody'
import ModalContent from 'components/Luxkit/Modal/ModalContent'
import OfferFilterBundleSidebarBottomPanel from '../../Sidebar/OfferFilterBundleSidebarBottomPanel'
import BodyText from 'components/Luxkit/Typography/BodyText'
import Caption from 'components/Luxkit/Typography/Caption'

interface Props {
  offer: App.Offer;
  bundledOffersState: OfferPageState['bundledOffers'];
  fetching?: boolean;
  checkIn?: moment.Moment;
  selectedDay?: App.CalendarDay;
  calendarError?: boolean;
  calendarAvailability: boolean;
  defaultDayPicker?: boolean;
  defaultMonth?: string;
  duration: number;
  rooms?: App.Occupants[];
  recommendationsShown: boolean;
  currentOfferId?: string;
  onBack?: () => void;
  onClose?: () => void;
  onApply?: () => void;
  activeFilter?: OfferPageFilterTypes;
  paginator?: PaginatorState;
}

function OfferBundleAndSaveCalendarSidebar(props: Props) {
  const {
    offer,
    bundledOffersState,
    fetching,
    selectedDay,
    checkIn,
    calendarError,
    calendarAvailability,
    defaultDayPicker,
    defaultMonth,
    duration,
    recommendationsShown,
    rooms,
    currentOfferId,
    onBack,
    onClose,
    onApply,
    activeFilter,
    paginator,
  } = props
  const onUserAnalyticsAction = useContext(UserAnalyticsActionContext)

  const bundledOffers = useMemo(() => Object.values(bundledOffersState ?? {}).filter(Boolean), [bundledOffersState])

  const [selectedMonth, setMonth] = useState<string | undefined>(defaultMonth ?? selectedDay?.monthKey)
  const dispatch = useOfferPageDispatch()
  const onMonthSelect = useCallback((monthKey: string) => {
    setMonth(monthKey)
  }, [])

  useEffect(() => {
    if (checkIn && selectedDay && !selectedDay.hotelAvailable) {
      // clear check in if the date we have selected is no longer available
      dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined, offerId: currentOfferId })
    }
  }, [checkIn, dispatch, selectedDay, currentOfferId])

  const onBackToMonth = useCallback(() => {
    onUserAnalyticsAction?.(CALENDAR_GA_EVENT_ACTION.YearView)
    dispatch({ type: OfferPageActions.setCheckInDate, checkIn: undefined, offerId: currentOfferId })
    setMonth(undefined)
  }, [onUserAnalyticsAction, dispatch, currentOfferId])

  const isBundleAndSaveOffer = bundledOffers.length > 0

  useDispatchPrecacheLere(true, offer, duration, rooms)

  const currentOfferIndex = bundledOffers.findIndex(o => o.offer?.id === paginator?.currentOfferId)
  const currentOffer = bundledOffers[currentOfferIndex]

  const dateLabel = useMemo(() => {
    const currentOffer = bundledOffers.find(o => o.offer?.id === paginator?.currentOfferId)
    const checkInDate = currentOffer?.checkInDate
    const checkOutDate = currentOffer?.checkOutDate
    const duration = currentOffer?.duration

    if (checkInDate && duration) {
      return `${checkInDate.format('DD MMM')} - ${checkInDate.clone().add(duration, 'days').format('DD MMM')}`
    }

    if (checkInDate && checkOutDate) {
      return `${checkInDate.format('DD MMM')} - ${checkOutDate.format('DD MMM')}`
    }

    return null
  }, [bundledOffers, paginator?.currentOfferId])

  return (<>
    <ModalHeader
      title={`Select check-in ${selectedMonth !== undefined ? 'dates' : 'month'}`}
      onCloseButtonClick={onClose}
      onBackButtonClick={onBack}
    >
      <Group direction="vertical" horizontalAlign="center" gap={4}>
        <Caption variant="medium" colour="neutral-three">
          Hotel {currentOfferIndex + 1} of {bundledOffers.length}
        </Caption>
        <BodyText variant="medium" weight="bold">
          {currentOffer?.offer?.property?.name}
        </BodyText>
        <Caption variant="medium" colour="neutral-three">
          {dateLabel ?? 'No dates selected'}
        </Caption>
      </Group>
      {!recommendationsShown && <Group direction="horizontal" verticalAlign="center" gap={8}>
        <OfferCalendarDurationSelect />
        <OfferCalendarBundlePackageSelect sidebar />
      </Group>}
    </ModalHeader>
    <ModalBody>
      <ModalContent>
        {recommendationsShown && <CalendarPickerRecommendationsSidebar />}
        {!recommendationsShown && <>
          {!fetching && calendarError && !calendarAvailability && <OfferCalendarNotAvailableMessage hasError={calendarError} />}
          {calendarAvailability && <>
            {selectedMonth !== undefined && !fetching && <OfferCalendarSidebarDayPicker
              onBackToMonth={onBackToMonth}
              initialMonth={selectedMonth}
              defaultDayPicker={defaultDayPicker}
              flightsOnly={offer.bundledWithFlightsOnly}
            />}
            {selectedMonth === undefined && <OfferCalendarMonthPicker
              onMonthSelect={onMonthSelect}
            />}
          </>
        }
          <LoadingIndicator visible={!calendarAvailability && isBundleAndSaveOffer} floating />
        </>}
      </ModalContent>
    </ModalBody>
    <OfferFilterBundleSidebarBottomPanel
      onClick={onApply}
      activeFilter={activeFilter}
    />
  </>
  )
}

export default withOfferPageAndReduxState((currentState, appState) => {
  const state = takeCurrentOfferState(currentState)

  return {
    paginator: state.paginator,
    currentOfferId: state.paginator?.currentOfferId,
    calendarAvailability: getCalendarMonths(currentState, appState).availableCount > 0,
    calendarError: state.offer?.packages.some(pkg => appState.calendar.calendarErrors[pkg.uniqueKey]),
    checkIn: state.checkInDate,
    fetching: isFetchingCalendars(state, appState),
    offer: state.offer,
    recommendationsShown: state.showRecommendationsView && shouldEnableCalendarRecommendations(appState, state.offer),
    bundledOffersState: state.bundledOffers,
    selectedDay: getSelectedOfferPageDay(state, appState),
    duration: state.duration,
    rooms: state.rooms,
    activeFilter: state.activeFilter,
  }
})(OfferBundleAndSaveCalendarSidebar)
